let sdkLoadPromise: Promise<void> | null = null

export function normalizeFacebookPageUrl(url: string): string {
  const trimmed = (url || '').trim()
  if (!trimmed) return ''

  const withProtocol = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`
  return withProtocol.replace(/\/+$/, '')
}

function getFb() {
  return (window as Window & { FB?: { XFBML?: { parse: (node?: HTMLElement) => void } } }).FB
}

function whenBrowserIdle(cb: () => void, timeout = 4000): void {
  if (typeof window === 'undefined') return
  const ric = (window as Window & {
    requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number
  }).requestIdleCallback
  if (typeof ric === 'function') {
    ric(cb, { timeout })
    return
  }
  const run = () => setTimeout(cb, 1500)
  if (document.readyState === 'complete') run()
  else window.addEventListener('load', run, { once: true })
}

export function loadFacebookSdk(locale = 'el_GR', version = 'v21.0'): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve()
  if (getFb()?.XFBML?.parse) return Promise.resolve()
  if (sdkLoadPromise) return sdkLoadPromise

  sdkLoadPromise = new Promise((resolve) => {
    const finish = () => resolve()

    if (document.getElementById('facebook-jssdk')) {
      const waitForFb = () => {
        if (getFb()?.XFBML?.parse) finish()
        else setTimeout(waitForFb, 100)
      }
      waitForFb()
      return
    }

    ;(window as Window & { fbAsyncInit?: () => void }).fbAsyncInit = () => finish()

    const script = document.createElement('script')
    script.id = 'facebook-jssdk'
    script.async = true
    script.defer = true
    script.crossOrigin = 'anonymous'
    script.src = `https://connect.facebook.net/${locale}/sdk.js#xfbml=1&version=${version}`
    script.onload = () => {
      if (getFb()?.XFBML?.parse) finish()
    }
    document.head.appendChild(script)
  })

  return sdkLoadPromise
}

/** Load SDK only after first paint / idle — keeps Facebook off the LCP critical path */
export function loadFacebookSdkWhenIdle(locale = 'el_GR', version = 'v21.0'): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve()
  if (getFb()?.XFBML?.parse) return Promise.resolve()
  return new Promise((resolve) => {
    whenBrowserIdle(() => {
      loadFacebookSdk(locale, version).then(resolve).catch(() => resolve())
    })
  })
}

export function ensureFbRoot(): void {
  if (typeof document === 'undefined') return
  if (!document.getElementById('fb-root')) {
    const root = document.createElement('div')
    root.id = 'fb-root'
    document.body.prepend(root)
  }
}

export async function parseFacebookEmbeds(root?: HTMLElement | null): Promise<void> {
  ensureFbRoot()
  await loadFacebookSdkWhenIdle()
  getFb()?.XFBML?.parse(root || undefined)
}

/** Observe element; load + parse FB only when embed is near viewport */
export function observeFacebookEmbed(root: HTMLElement | null): () => void {
  if (!root || typeof IntersectionObserver === 'undefined') {
    void parseFacebookEmbeds(root)
    return () => {}
  }
  let done = false
  const io = new IntersectionObserver(
    (entries) => {
      if (done) return
      if (!entries.some((e) => e.isIntersecting)) return
      done = true
      io.disconnect()
      void parseFacebookEmbeds(root)
    },
    { rootMargin: '200px 0px' },
  )
  io.observe(root)
  return () => io.disconnect()
}
